import React, { useState, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";
import styled from "@emotion/styled";
// import Loader from "../../../components/icons/Loader";
import Checkbox from "../../../components/forms/QuestionnaireCheckbox";
import { useGetCurrentUserQuery } from "../../../api/users";
import {
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
} from "../../../api/answers";
import {
  QuestionWithRelations,
  QuestionOption,
} from "../../../../../api/src/types";
// import useIsMobile from "../../../hooks/useIsMobile";
import {
  InputWrapper,
  ErrorWrapper /*IconContainer */,
} from "./SharedFormStyles";
import { useSelectedOptions } from "../utils/SelectedOptionsContext";

const CheckboxContainer = styled.div`
  flex: 1;
`;

interface CheckboxInputProps {
  question: QuestionWithRelations;
}

const CheckboxInput: React.FC<CheckboxInputProps> = ({ question }) => {
  // const isMobile = useIsMobile();
  const { setSelectedOptionIds: setContextSelectedOptionIds } =
    useSelectedOptions();
  // Track selected option IDs for the current question only
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(
    question.answers[0]?.answerQuestionOptions.map((a) => a.questionOptionId) ??
      [],
  );
  const [hasAnswer, setHasAnswer] = useState(question.answers.length > 0);
  /* id of the option whose save is in-flight */
  // const [pendingId, setPendingId] = useState<string | null>(null);
  const [errorId, setErrorId] = useState<string | null>(null);

  const { data: user } = useGetCurrentUserQuery();
  const [
    createAnswer,
    {
      /*isLoading: isCreating, */ isError: isCreateError,
      reset: resetCreateAnswer,
    },
  ] = useCreateAnswerMutation();
  const [
    updateAnswer,
    {
      /*isLoading: isUpdating, */ isError: isUpdateError,
      reset: resetUpdateAnswer,
    },
  ] = useUpdateAnswerMutation();

  useEffect(() => {
    setHasAnswer(question.answers.length > 0);
    // Update selectedOptionIds when question answers change
    const serverSelectedIds =
      question.answers[0]?.answerQuestionOptions.map(
        (a) => a.questionOptionId,
      ) ?? [];
    setSelectedOptionIds(serverSelectedIds);
    // Also sync context state
    setContextSelectedOptionIds(serverSelectedIds);
  }, [question, setContextSelectedOptionIds]);

  const save = useDebouncedCallback(
    async (optionIds: string[], selectedId: string) => {
      if (!user) return;
      setErrorId(null);
      // setPendingId(selectedId);

      const payload = {
        questionId: question.id,
        userId: user.id,
        questionOptionIds: optionIds,
      };

      try {
        if (!hasAnswer) {
          if (optionIds.length) {
            await createAnswer(payload).unwrap();
            setHasAnswer(true);
          }
        } else {
          await updateAnswer(payload).unwrap();
        }
      } catch (err) {
        console.error(err);
        setErrorId(selectedId);
      } /* finally {
        setPendingId(null);
      } */
    },
    300,
  );

  // Handle selection toggle
  const handleToggle = (optionId: string) => {
    resetCreateAnswer();
    resetUpdateAnswer();

    setSelectedOptionIds((prevSelected) => {
      // Check if option is already selected
      const isCurrentlySelected = prevSelected.includes(optionId);

      // If selected, remove it; otherwise, add it
      const newSelected = isCurrentlySelected
        ? prevSelected.filter((id) => id !== optionId)
        : [...prevSelected, optionId];

      // Update context for instant protip display
      setContextSelectedOptionIds(newSelected);
      void save(newSelected, optionId);
      return newSelected;
    });
  };

  return (
    <>
      {question.questionOptions.map((option: QuestionOption) => {
        // Check if this option is selected
        const isChecked = selectedOptionIds.includes(option.id);
        // const isLoading = pendingId === option.id && (isCreating || isUpdating);
        const hasError =
          errorId === option.id && (isUpdateError || isCreateError); // local error

        return (
          <React.Fragment key={option.id}>
            <InputWrapper>
              <CheckboxContainer>
                <Checkbox
                  label={option.value}
                  id={option.id}
                  isChecked={isChecked}
                  onToggle={() => {
                    handleToggle(option.id);
                    // setPendingId(option.id);
                  }}
                  data-testid="checkbox-container"
                />
              </CheckboxContainer>
              {/* Commenting out the loader for now as per the request of the design team */}
              {/* {isLoading && (
                <IconContainer>
                  <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
                </IconContainer>
              )} */}
            </InputWrapper>
            {hasError && (
              <ErrorWrapper>
                Error saving answer, please refresh the page and try again
              </ErrorWrapper>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default CheckboxInput;
